import CustomDateTimePicker from 'Components/CustomDateTimePicker'
import CustomLoadingButton from 'Components/CustomLoadingButton'
import PTAFormField from 'Components/PTAFormField'
import ServiceProviderFieldErrors from 'Components/ServiceProviderFieldErrors'
import StyledForm from 'Components/StyledForm/StyledForm'
import StyledLabel from 'Components/StyledLabel'
import SPMAllowedApiClients from 'Services/ServiceProviderManagement/Features/AllowedClient/Components/SPMAllowedApiClients'
import { SELECTED_SERVICE_PROVIDER_PARAMETER_KEY } from 'Services/ServiceProviderManagement/Features/ServiceProvider/Hooks/useServiceProvidersWithParams'
import {
  useCreateSingleServiceProviderMutation,
  useUpdateSingleServiceProviderMutation
} from 'Services/ServiceProviderManagement/Features/ServiceProvider/serviceProviderApi'
import {
  type ServiceProvider,
  ServiceProviderType
} from 'Services/ServiceProviderManagement/Features/ServiceProvider/types'
import { type Translation, useTypedSelector } from 'Store'
import type {
  ServiceProviderManagementError,
  ServiceProviderManagementViolation
} from 'Util/ApiClient'
import { CopyObjectKeys, useCopyObjectContext } from 'Util/Context/copyObjectContext'
import {
  IS_HSL_SERVICE_ENVIRONMENT,
  IS_WALTTI_SERVICE_ENVIRONMENT,
  PTA_PARAM_KEY
} from 'Util/constants'
import { formatToUTCISO, formatViolationErrorField } from 'Util/formatters'
import SnackbarUtils from 'Util/Snackbar'
import { appendQueryParamsToUrl } from 'Util/url'
import {
  Box,
  Checkbox,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField
} from '@mui/material'
import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { t } from 'ttag'

type FieldErrors = {
  [key: string]: string[]
}

const fieldErrorsDefaultState: FieldErrors = {
  service_provider_id: [],
  type: [],
  start_date: [],
  name: [],
  customer_reference_name: [],
  business_id: [],
  s365t_account_id: [],
  billed_at_sales_point_level: [],
  transfer_sales_to_financial_system: [],
  register_file_path: [],
  register_file_name: [],
  register_ok_file_name: [],
  register_not_ok_file_name: [],
  vrk_queries_enabled: [],
  cpu_url: [],
  pusatec_channel: [],
  source: []
}

interface ServiceProviderFormProps {
  serviceProvider?: ServiceProvider
  successBaseUrl: string
}

export default function ServiceProviderForm({
  serviceProvider,
  successBaseUrl
}: Readonly<ServiceProviderFormProps>) {
  const navigate = useNavigate()
  const isEdit = !!serviceProvider
  const { user } = useTypedSelector(state => state.user)

  const [createSingleServiceProvider, createStatus] = useCreateSingleServiceProviderMutation()
  const [updateSingleServiceProvider, updateStatus] = useUpdateSingleServiceProviderMutation()

  const mutationStatus = isEdit ? updateStatus : createStatus
  const { isLoading: isSubmitLoading } = mutationStatus

  const { getCopiedObject, clearCopiedObject } = useCopyObjectContext()

  const serviceProviderDefaultValues = {
    active: true,
    ...(IS_HSL_SERVICE_ENVIRONMENT
      ? {
          billed_at_sales_point_level: false,
          transfer_sales_to_financial_system: false
        }
      : {
          vrk_queries_enabled: false
        })
  }

  const copiedServiceProviderData = isEdit
    ? null
    : getCopiedObject<ServiceProvider>(CopyObjectKeys.SERVICE_PROVIDER)

  const [serviceProviderDetails, setServiceProviderDetails] = useState<Partial<ServiceProvider>>(
    (isEdit && serviceProvider) || copiedServiceProviderData || { ...serviceProviderDefaultValues }
  )
  const [fieldErrors, setFieldErrors] = useState(fieldErrorsDefaultState)

  const [selectedReadClients, setSelectedReadClients] = useState<string[]>(
    serviceProviderDetails?.client_read ?? []
  )
  const [selectedWriteClients, setSelectedWriteClients] = useState<string[]>(
    serviceProviderDetails?.client_write ?? []
  )

  useEffect(() => {
    if (copiedServiceProviderData) {
      clearCopiedObject(CopyObjectKeys.SERVICE_PROVIDER)
    }
  }, [])

  useEffect(() => {
    if (isEdit && serviceProvider) {
      setServiceProviderDetails(serviceProvider)
    }
  }, [serviceProvider, isEdit])

  const handleChange = (
    value: string | string[] | Translation[] | number | boolean | undefined,
    field: keyof ServiceProvider
  ) => {
    if (value !== undefined) {
      setServiceProviderDetails(
        prevState =>
          ({
            ...prevState,
            [field]: value
          }) as ServiceProvider
      )
    }
    if (field === 's365t_account_id') {
      setServiceProviderDetails(
        prevState =>
          ({
            ...prevState,
            s365t_account_id: value === '' ? null : value,
            billed_at_sales_point_level: prevState.s365t_account_id
              ? prevState.billed_at_sales_point_level
              : false,
            transfer_sales_to_financial_system: prevState.s365t_account_id
              ? prevState.transfer_sales_to_financial_system
              : false
          }) as ServiceProvider
      )
    }
  }

  const handleDateTimePickerChange = (value: Date | null, name: string) => {
    let formattedValue = ''
    if (value instanceof Date) {
      formattedValue = formatToUTCISO(value)
    }

    setServiceProviderDetails(prevState => ({
      ...prevState,
      [name]: formattedValue
    }))
  }

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    const serviceProviderData = {
      ...serviceProviderDetails,
      client_read: selectedReadClients,
      client_write: selectedWriteClients
    }

    const mutation = isEdit
      ? updateSingleServiceProvider({
          ...serviceProviderData,
          serviceProviderId: serviceProvider.service_provider_id
        })
      : createSingleServiceProvider({
          ...serviceProviderData,
          pta: IS_HSL_SERVICE_ENVIRONMENT ? user?.pta : serviceProviderData.pta
        })

    mutation
      .unwrap()
      .then((serviceProviderResponse: ServiceProvider) => {
        SnackbarUtils.success(t`Service provider successfully saved.`)
        navigate(
          appendQueryParamsToUrl(successBaseUrl, {
            [SELECTED_SERVICE_PROVIDER_PARAMETER_KEY]: serviceProviderResponse.service_provider_id,
            [PTA_PARAM_KEY]: serviceProviderResponse.pta
          })
        )
      })
      .catch(({ data: errorResponse }: { data: ServiceProviderManagementError }) => {
        const { violations, message } = errorResponse
        if (violations && violations.length > 0) {
          setValidationErrors(violations)
        } else if (message) {
          SnackbarUtils.error(message)
        } else {
          SnackbarUtils.error(t`There was an error saving the service provider.`)
        }
      })
  }

  const handleServiceProviderTypeChange = (value: string, field: keyof ServiceProvider) => {
    setServiceProviderDetails(
      prevState =>
        ({
          ...prevState,
          [field]: value,
          register_file_name:
            value === ServiceProviderType.SERVICE_POINT ? prevState.register_file_name : null,
          register_file_path:
            value === ServiceProviderType.SERVICE_POINT ? prevState.register_file_path : null,
          register_ok_file_name:
            value === ServiceProviderType.SERVICE_POINT ? prevState.register_ok_file_name : null,
          register_not_ok_file_name:
            value === ServiceProviderType.SERVICE_POINT ? prevState.register_not_ok_file_name : null
        }) as ServiceProvider
    )
  }

  function setValidationErrors(violations: ServiceProviderManagementViolation[]) {
    setFieldErrors(() => {
      // Reset the field errors to default state, so that the old errors won't be shown
      const updatedFieldErrors = { ...fieldErrorsDefaultState }
      for (const violation of violations) {
        updatedFieldErrors[formatViolationErrorField(violation.field)] = [violation.message]
      }
      return updatedFieldErrors
    })
  }

  const commonServiceProviderTypes = [
    {
      label: t`Traffic contractor`,
      value: ServiceProviderType.TRAFFIC_CONTRACTOR
    },
    {
      label: t`U Traffic contractor`,
      value: ServiceProviderType.U_TRAFFIC_CONTRACTOR
    },
    {
      label: t`Sales point`,
      value: ServiceProviderType.SALES_POINT
    },
    {
      label: t`Service point`,
      value: ServiceProviderType.SERVICE_POINT
    },
    {
      label: t`MaaS`,
      value: ServiceProviderType.MAAS
    },
    {
      label: t`Other`,
      value: ServiceProviderType.OTHER
    }
  ]

  const walttiServiceProviderTypes = [
    ...commonServiceProviderTypes,
    {
      label: t`Ticket machine`,
      value: ServiceProviderType.TICKET_MACHINE
    },
    {
      label: t`Webshop`,
      value: ServiceProviderType.WEBSHOP
    },
    {
      label: t`Mobile`,
      value: ServiceProviderType.MOBILE
    },
    {
      label: t`Event ticket`,
      value: ServiceProviderType.EVENT_TICKET
    }
  ]

  const hslServiceProviderTypes = [
    ...commonServiceProviderTypes,
    {
      label: t`HSL digital channel`,
      value: ServiceProviderType.HSL_DIGITAL_CHANNEL
    }
  ]

  const serviceProviderTypes = IS_HSL_SERVICE_ENVIRONMENT
    ? hslServiceProviderTypes
    : walttiServiceProviderTypes

  return (
    <Grid container spacing={2} mb={4}>
      <Grid size={12}>
        <StyledForm onSubmit={handleSubmit} isDisabled={isSubmitLoading}>
          {!isEdit && IS_WALTTI_SERVICE_ENVIRONMENT && (
            <Box>
              <PTAFormField
                value={serviceProviderDetails.pta}
                onChange={value => handleChange(value, 'pta')}
              />
            </Box>
          )}
          <Box sx={{ display: 'flex' }}>
            {!isEdit && (
              <Box>
                <TextField
                  name="service_provider_id"
                  required
                  label={t`Service provider ID`}
                  size="small"
                  sx={{ mr: 3, minWidth: 240 }}
                  value={serviceProviderDetails?.service_provider_id ?? ''}
                  error={fieldErrors.service_provider_id.length > 0}
                  onChange={e => handleChange(e.target.value, 'service_provider_id')}
                  slotProps={{ htmlInput: { maxLength: 9 } }}
                />
                <ServiceProviderFieldErrors
                  error={fieldErrors.service_provider_id}
                  identifier="service_provider_id"
                />
              </Box>
            )}
            <Box>
              <TextField
                name="name"
                required
                label={t`Name`}
                size="small"
                sx={{ mr: 3, minWidth: 240 }}
                value={serviceProviderDetails?.name ?? ''}
                error={fieldErrors.name.length > 0}
                onChange={e => handleChange(e.target.value, 'name')}
              />
              <ServiceProviderFieldErrors error={fieldErrors.name} identifier="name" />
            </Box>
            <FormControl sx={{ minWidth: 240, mr: 3 }} required>
              <InputLabel size="small">{t`Type`}</InputLabel>
              <Select
                name="type"
                required
                size="small"
                label={t`Type`}
                value={serviceProviderDetails?.type ?? ''}
                error={fieldErrors.type?.length > 0}
                onChange={e => handleServiceProviderTypeChange(e.target.value, 'type')}
                sx={{
                  minWidth: 280
                }}
              >
                {serviceProviderTypes.map(({ label, value }) => (
                  <MenuItem key={value} value={value}>
                    {label}
                  </MenuItem>
                ))}
              </Select>
              <ServiceProviderFieldErrors error={fieldErrors.type} identifier="type" />
            </FormControl>
          </Box>
          <StyledLabel>{t`Start date`}</StyledLabel>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <CustomDateTimePicker
              onChange={handleDateTimePickerChange}
              name="start_date"
              value={
                serviceProviderDetails?.start_date
                  ? new Date(serviceProviderDetails?.start_date)
                  : null
              }
              dateToCompare={
                serviceProviderDetails?.start_date
                  ? new Date(serviceProviderDetails?.start_date)
                  : null
              }
              dateToCompareFieldName="start_date"
              isStartDate={true}
              label={t`Start date`}
              isRequired={true}
              isError={fieldErrors.start_date?.length > 0}
            />
            <ServiceProviderFieldErrors error={fieldErrors.start_date} identifier="start_date" />
          </Box>
          <Box>
            <StyledLabel>{t`Business information`}</StyledLabel>
            <TextField
              name="business_id"
              label={t`Business id`}
              size="small"
              value={serviceProviderDetails?.business_id ?? ''}
              error={fieldErrors.business_id.length > 0}
              onChange={e => handleChange(e.target.value, 'business_id')}
              sx={{ minWidth: 240, mr: 3 }}
            />
            <ServiceProviderFieldErrors error={fieldErrors.business_id} identifier="business_id" />
          </Box>
          {IS_HSL_SERVICE_ENVIRONMENT && (
            <>
              <TextField
                name="s365t_account_id"
                label={t`S365 account id`}
                size="small"
                value={serviceProviderDetails?.s365t_account_id ?? ''}
                error={fieldErrors.s365t_account_id.length > 0}
                onChange={e =>
                  handleChange(e.target.value.replaceAll(/\D/g, ''), 's365t_account_id')
                }
                slotProps={{ htmlInput: { inputMode: 'numeric' } }}
                sx={{ minWidth: 240, mr: 3 }}
              />
              <ServiceProviderFieldErrors
                error={fieldErrors.s365t_account_id}
                identifier="s365t_account_id"
              />
              <Box>
                <TextField
                  name="customer_reference_name"
                  required
                  label={t`Customer reference name`}
                  size="small"
                  value={serviceProviderDetails?.customer_reference_name ?? ''}
                  error={fieldErrors.customer_reference_name.length > 0}
                  onChange={e => handleChange(e.target.value, 'customer_reference_name')}
                  sx={{ minWidth: 240, mr: 3 }}
                />
                <ServiceProviderFieldErrors
                  error={fieldErrors.customer_reference_name}
                  identifier="customer_reference_name"
                />
              </Box>
              <Box display={'flex'} flexDirection={'column'}>
                <FormControlLabel
                  sx={{ width: 'fit-content' }}
                  control={
                    <Checkbox
                      checked={serviceProviderDetails.billed_at_sales_point_level}
                      disabled={!serviceProviderDetails.s365t_account_id}
                      onChange={e => handleChange(e.target.checked, 'billed_at_sales_point_level')}
                      name="billed_at_sales_point_level"
                      color="primary"
                    />
                  }
                  label={t`Billed at sales point level`}
                />
                <ServiceProviderFieldErrors
                  error={fieldErrors.billed_at_sales_point_level}
                  identifier="billed_at_sales_point_level"
                />
                <FormControlLabel
                  sx={{ width: 'fit-content' }}
                  control={
                    <Checkbox
                      checked={serviceProviderDetails.transfer_sales_to_financial_system}
                      disabled={!serviceProviderDetails.s365t_account_id}
                      onChange={e =>
                        handleChange(e.target.checked, 'transfer_sales_to_financial_system')
                      }
                      name="transfer_sales_to_financial_system"
                      color="primary"
                    />
                  }
                  label={t`Transfer sales to financial system`}
                />
                <ServiceProviderFieldErrors
                  error={fieldErrors.transfer_sales_to_financial_system}
                  identifier="transfer_sales_to_financial_system"
                />
              </Box>
            </>
          )}
          {serviceProviderDetails.type === ServiceProviderType.SERVICE_POINT &&
            IS_HSL_SERVICE_ENVIRONMENT && (
              <Box>
                <StyledLabel>{t`Other`}</StyledLabel>
                <TextField
                  name="register_file_path"
                  required
                  label={t`Register file path`}
                  size="small"
                  value={serviceProviderDetails?.register_file_path}
                  error={fieldErrors.register_file_path.length > 0}
                  onChange={e => handleChange(e.target.value, 'register_file_path')}
                  sx={{ minWidth: 240, mr: 3 }}
                />
                <ServiceProviderFieldErrors
                  error={fieldErrors.register_file_path}
                  identifier="register_file_path"
                />
                <TextField
                  name="register_file_name"
                  required
                  label={t`Register file name`}
                  size="small"
                  value={serviceProviderDetails?.register_file_name}
                  error={fieldErrors.register_file_name.length > 0}
                  onChange={e => handleChange(e.target.value, 'register_file_name')}
                  sx={{ minWidth: 240, mr: 3 }}
                />
                <ServiceProviderFieldErrors
                  error={fieldErrors.register_file_name}
                  identifier="register_file_name"
                />
                <TextField
                  name="register_ok_file_name"
                  required
                  label={t`Register OK file name`}
                  size="small"
                  value={serviceProviderDetails?.register_ok_file_name}
                  error={fieldErrors.register_ok_file_name.length > 0}
                  onChange={e => handleChange(e.target.value, 'register_ok_file_name')}
                  sx={{ minWidth: 240, mr: 3 }}
                />
                <ServiceProviderFieldErrors
                  error={fieldErrors.register_ok_file_name}
                  identifier="register_ok_file_name"
                />
                <TextField
                  name="register_not_ok_file_name"
                  required
                  label={t`Register not OK file name`}
                  size="small"
                  value={serviceProviderDetails?.register_not_ok_file_name}
                  error={fieldErrors.register_not_ok_file_name.length > 0}
                  onChange={e => handleChange(e.target.value, 'register_not_ok_file_name')}
                  sx={{ minWidth: 240, mr: 3 }}
                />
                <ServiceProviderFieldErrors
                  error={fieldErrors.register_not_ok_file_name}
                  identifier="register_not_ok_file_name"
                />
              </Box>
            )}
          {IS_WALTTI_SERVICE_ENVIRONMENT && (
            <Box>
              <StyledLabel>{t`Other`}</StyledLabel>
              <TextField
                name="cpu_url"
                label={t`CPU url`}
                size="small"
                value={serviceProviderDetails?.cpu_url}
                error={fieldErrors.cpu_url.length > 0}
                onChange={e => handleChange(e.target.value, 'cpu_url')}
                sx={{ minWidth: 240, mr: 3 }}
              />
              <ServiceProviderFieldErrors error={fieldErrors.cpu_url} identifier="cpu_url" />
              <TextField
                name="pusatec_channel"
                label={t`Pusatec channel`}
                size="small"
                value={serviceProviderDetails?.pusatec_channel}
                error={fieldErrors.pusatec_channel.length > 0}
                onChange={e => handleChange(e.target.value, 'pusatec_channel')}
                sx={{ minWidth: 300, mr: 3 }}
              />
              <ServiceProviderFieldErrors
                error={fieldErrors.pusatec_channel}
                identifier="pusatec_channel"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={serviceProviderDetails.vrk_queries_enabled}
                    onChange={e => handleChange(e.target.checked, 'vrk_queries_enabled')}
                    name="vrk_queries_enabled"
                    color="primary"
                  />
                }
                label={t`VRK queries enabled`}
              />
              <ServiceProviderFieldErrors
                error={fieldErrors.vrk_queries_enabled}
                identifier="vrk_queries_enabled"
              />
            </Box>
          )}
          <Box>
            <StyledLabel>{t`Source`}</StyledLabel>
            <TextField
              name="source"
              label={t`Source`}
              size="small"
              value={serviceProviderDetails?.source ?? ''}
              error={fieldErrors.source.length > 0}
              onChange={e => handleChange(e.target.value, 'source')}
              slotProps={{ htmlInput: { maxLength: 256 } }}
              sx={{ minWidth: 300, mr: 3 }}
            />
            <ServiceProviderFieldErrors error={fieldErrors.source} identifier="source" />
          </Box>
          <Box sx={{ maxWidth: '750px', py: 2 }}>
            <SPMAllowedApiClients
              selectedReadClients={selectedReadClients}
              selectedWriteClients={selectedWriteClients}
              setSelectedReadClients={setSelectedReadClients}
              setSelectedWriteClients={setSelectedWriteClients}
            />
          </Box>
          <CustomLoadingButton
            isLoading={isSubmitLoading}
            type="submit"
            size="large"
            iconPosition="end"
          >
            {t`Save service provider`}
          </CustomLoadingButton>
        </StyledForm>
      </Grid>
    </Grid>
  )
}
